import { Amenity } from "@/components/ui/Amenity";
import { Button } from "@/components/ui/Button";
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/Drawer";
import { Label } from "@/components/ui/Label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/Popover";
import { Separator } from "@/components/ui/Separator";
import { useSearch } from "@/contexts/SearchContext";
import { PropertyAmenity } from "@/enums/property.enum";
import clsx from "clsx";
import { ChevronDown } from "lucide-react";
import { useTranslations } from "next-intl";
import { RefObject, useRef } from "react";
import { useOnClickOutside, useToggle } from "usehooks-ts";
import { PropertyFilterProps } from "./type";

export const PropertyAmenitiesFilter = ({
  variant,
  title,
}: PropertyFilterProps) => {
  const popoverContentRef = useRef<HTMLDivElement>(null);
  const drawerContentRef = useRef<HTMLDivElement>(null);

  const t = useTranslations("shared");
  const [open, toggleOpen, setOpen] = useToggle();

  useOnClickOutside(popoverContentRef as RefObject<HTMLElement>, (event) => {
    const target = event.target as HTMLElement;
    const isSelectContent = target.closest('[role="listbox"]');

    if (!isSelectContent) {
      setOpen(false);
    }
  });

  useOnClickOutside(drawerContentRef as RefObject<HTMLElement>, (event) => {
    const target = event.target as HTMLElement;
    const isSelectContent = target.closest('[role="listbox"]');

    if (!isSelectContent) {
      toggleOpen();
    }
  });

  const { amenities, toggleAmenity, updateAmenity } = useSearch();

  const handleClear = () => {
    updateAmenity(null);
  };

  const onAmenitiesChange = (value: PropertyAmenity) => {
    toggleAmenity(value);
  };

  const Content = () => {
    const FILTER_AMENITIES = [
      PropertyAmenity.CENTRAL_AIR_CONDITIONING,
      PropertyAmenity.MAIDS_ROOM,
      PropertyAmenity.BALCONY,
      PropertyAmenity.SHARED_POOL,
      PropertyAmenity.SHARED_SPA,
      PropertyAmenity.SHARED_GYM,
      PropertyAmenity.CONCIERGE_SERVICE,
      PropertyAmenity.COVERED_PARKING,
      PropertyAmenity.WATERFRONT_VIEW,
      PropertyAmenity.VIEW_OF_LANDMARK,
      PropertyAmenity.PETS_ALLOWED,
      PropertyAmenity.STUDY,
      PropertyAmenity.PRIVATE_GARDEN,
      PropertyAmenity.PRIVATE_POOL,
      PropertyAmenity.PRIVATE_GYM,
      PropertyAmenity.PRIVATE_JACUZZI,
      PropertyAmenity.BUILT_IN_WARDROBES,
      PropertyAmenity.WALK_IN_CLOSET,
      PropertyAmenity.KITCHEN_APPLIANCES,
      PropertyAmenity.MAID_SERVICE,
      PropertyAmenity.CHILDREN_PLAY_AREA,
      PropertyAmenity.CHILDREN_POOL,
      PropertyAmenity.BBQ_AREA,
    ];

    return (
      <>
        <div
          className={clsx(
            "flex flex-col sm:flex-row w-full items-center gap-2",
            {
              "py-1 sm:py-3": title,
            }
          )}
        >
          <div className="space-y-2 w-full">
            {variant === "plain" && <Label>{t("words.amenities")}</Label>}
            <div className="flex flex-wrap gap-2 max-w-lg">
              {FILTER_AMENITIES.map((amenity) => (
                <Button
                  variant={amenities?.includes(amenity) ? "active" : "outline"}
                  key={amenity}
                  onClick={() => onAmenitiesChange(amenity)}
                  className="w-fit capitalize truncate"
                >
                  <Amenity amenity={amenity} />
                </Button>
              ))}
            </div>
          </div>
        </div>

        {variant !== "plain" && (
          <>
            <Separator />
            <div className="space-x-2 flex items-end w-full">
              <Button
                variant={"tertiary"}
                onClick={handleClear}
                className="w-full"
              >
                {t("filters.cta.clear")}
              </Button>

              <Button
                variant={"secondary"}
                onClick={() => setOpen(false)}
                className="w-full"
              >
                {t("filters.cta.done")}
              </Button>
            </div>
          </>
        )}
      </>
    );
  };

  switch (variant) {
    case "popover":
      return (
        <Popover open={open}>
          <PopoverTrigger asChild className="h-full text-base">
            <Button
              variant={amenities?.length || 0 > 0 ? "active" : "outline"}
              onClick={toggleOpen}
              className="min-w-fit"
            >
              {amenities?.length || 0 > 0
                ? `(${amenities?.length}) ${t("words.amenities")}`
                : t("words.amenities")}
              <ChevronDown className="size-4.5" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-full flex flex-col gap-4"
            ref={popoverContentRef}
          >
            <Label className="font-medium">{t("words.amenities")}</Label>
            <Content />
          </PopoverContent>
        </Popover>
      );
    case "plain":
      return <Content />;
    case "drawer":
      return (
        <Drawer open={open} key={"amenities-drawer"}>
          <DrawerTrigger asChild className="h-full text-base">
            <Button
              variant={amenities?.length || 0 > 0 ? "active" : "outline"}
              onClick={toggleOpen}
            >
              {amenities
                ? `(${amenities.length}) ${t("words.amenities")}`
                : t("words.amenities")}
              <ChevronDown className="size-4.5" />
            </Button>
          </DrawerTrigger>
          <DrawerContent
            className="w-full flex flex-col gap-4 p-2"
            ref={drawerContentRef}
          >
            <DrawerTitle>{t("filters.amenities.title")}</DrawerTitle>
            <Content />
          </DrawerContent>
        </Drawer>
      );
  }
};
